import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Business as OrgIcon } from "@mui/icons-material";
import {
  getInvitationByToken,
  acceptInvitation,
} from "../services/Organization";
import { fetchOrganizations, setCurrentOrg } from "../redux/slices/orgSlice";

const ROLE_COLORS = {
  OWNER: "error",
  ADMIN: "warning",
  DEVELOPER: "primary",
  VIEWER: "default",
};

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.user);
  const user = useSelector((state) => state.user.user);
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await getInvitationByToken(token);
        setInvitation(response.data);
      } catch (err) {
        setError(
          err.response?.data?.error || "Invitation not found or expired",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // Save invite token and redirect to login/signup
      localStorage.setItem("pendingInviteToken", token);
      // Pre-fill the invited email so they sign in with the correct account
      const inviteEmail = invitation?.email || "";
      navigate(
        `/auth?redirect=invite&email=${encodeURIComponent(inviteEmail)}`,
      );
      return;
    }

    setAccepting(true);
    setError(null);
    try {
      const response = await acceptInvitation(token);
      setSuccess(true);

      // Refresh org list and switch to the accepted org
      const orgsResult = await dispatch(fetchOrganizations()).unwrap();
      const acceptedOrg = response.data?.org;
      if (acceptedOrg) {
        const fullOrg = orgsResult.find((o) => o.id === acceptedOrg.id);
        if (fullOrg) {
          dispatch(setCurrentOrg(fullOrg));
        }
      }

      // Redirect to dashboard after a short delay
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  // Check if the logged-in user's email matches the invitation email
  const emailMismatch =
    isAuthenticated &&
    invitation?.email &&
    user?.email &&
    user.email.toLowerCase() !== invitation.email.toLowerCase();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isExpired = invitation?.status === "EXPIRED";
  const isInvalid = !invitation || invitation.status === "REVOKED";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "80vh",
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 440, width: "100%", textAlign: "center" }}>
        {error && !invitation && (
          <>
            <Typography variant="h6" color="error" gutterBottom>
              Invalid Invitation
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button variant="outlined" onClick={() => navigate("/")}>
              Go Home
            </Button>
          </>
        )}

        {invitation && !isInvalid && (
          <>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "primary.main",
                mx: "auto",
                mb: 2,
              }}
              src={invitation.org?.avatarUrl || undefined}
            >
              <OrgIcon />
            </Avatar>

            <Typography variant="h5" fontWeight={600} gutterBottom>
              Join {invitation.org?.name}
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 1 }}>
              {invitation.inviter?.firstName} {invitation.inviter?.lastName}{" "}
              invited you to join as
            </Typography>

            <Chip
              label={invitation.role}
              color={ROLE_COLORS[invitation.role]}
              sx={{ mb: 3, textTransform: "capitalize" }}
            />

            {isExpired ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This invitation has expired. Please ask the organization admin
                to send a new one.
              </Alert>
            ) : success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                You've joined {invitation.org?.name}! Redirecting to
                dashboard...
              </Alert>
            ) : (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {emailMismatch && (
                  <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
                    This invitation was sent to{" "}
                    <strong>{invitation.email}</strong>, but you're signed in as{" "}
                    <strong>{user.email}</strong>. Please sign out and sign in
                    with the correct account.
                  </Alert>
                )}

                {isAuthenticated && !emailMismatch && invitation?.email && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Accepting as <strong>{user?.email}</strong>
                  </Typography>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={
                    emailMismatch
                      ? () => navigate("/auth?redirect=invite")
                      : handleAccept
                  }
                  disabled={accepting}
                >
                  {accepting ? (
                    <CircularProgress size={24} />
                  ) : emailMismatch ? (
                    "Switch Account"
                  ) : isAuthenticated ? (
                    "Accept Invitation"
                  ) : (
                    "Sign in to Accept"
                  )}
                </Button>
              </>
            )}
          </>
        )}

        {isInvalid && !error && (
          <>
            <Typography variant="h6" color="error" gutterBottom>
              Invitation Revoked
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              This invitation has been revoked by the organization admin.
            </Typography>
            <Button variant="outlined" onClick={() => navigate("/")}>
              Go Home
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
